"use client";
import { createTheme, } from '@aws-amplify/ui-react';

// Inter is self-hosted with next/font in app/layout.tsx, which defines --font-inter on <html>.
const FONT_STACK = "var(--font-inter), 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";

// Step 1: Create a new Theme with your custom values
export const theme = createTheme({
    name: 'moderatepoliticians-theme',
    tokens: {
      colors: {
        brand: {
          primary: { value: '#3ABA60' }, // Custom Primary Color
          secondary: { value: '#2da18a' }, // Custom Secondary Color
          light_primary_green: {value: '#D8F3E0' },
          moderate_purple: {value: '#8b82f1'},
          secondary_bar: {value: "#7166EE"},
          white: { value: '#FFFFFF'},
          special_black: {value: '#353535'},
          special_gray: {value: '#595858'},
          light_gray: {value: 'gray'},
          lighter_gray: {value: '#D0D0D0'},
          background: { value: '#F3F6F9' },
          hover_color : {value: '#f5f5f5'},
          custom_purple : {value: '#7166EE'},
          custom_purple_shade : {value: '#7166EE25'},
          full_transparent_black: {value: '#00000000'},
          soft_white : {value: '#EBEBEB'},
          card_background: {value: '#FFFFFF'},
          explore_issues_background: {value: "#FFFFFF"},
          explore_issues_text: {value: '#3ABA60'},
          explore_issues_button_background: {value: '#3ABA60'},
          nav_header: {value: "#353535"},
          chart_color: {value: '#353535'},
          // Neutral UI surface tokens (not used for bias colors)
          primary_hover: { value: '#2E9E50' },                  // pressed/hover state of brand.primary
          primary_soft: { value: 'rgba(58, 186, 96, 0.12)' },   // tinted chips, icon tiles, hovers
          purple_soft: { value: 'rgba(113, 102, 238, 0.12)' },
          surface_muted: { value: '#F7F9FB' },                  // inset panels inside cards
          border_subtle: { value: 'rgba(15, 23, 42, 0.08)' },   // hairline card borders
          border_strong: { value: 'rgba(15, 23, 42, 0.16)' },
        },
        font: {
          primary: { value: '{colors.brand.special_black}' },
          secondary: {value: '{colors.brand.special_black}'},
          tertiary: { value: '{colors.brand.special_gray}' },
        },
        // Amplify's default accent palette is teal; map it to the brand green so loaders,
        // default links and focus borders match the rest of the site.
        primary: {
          10: { value: '#EAF8EE' },
          20: { value: '#D8F3E0' },
          40: { value: '#9CDDB0' },
          60: { value: '#5FC981' },
          80: { value: '#3ABA60' },
          90: { value: '#2E9E50' },
          100: { value: '#237A3E' },
        },
      },
      fonts: {
        default: {
          variable: { value: FONT_STACK },
          static: { value: FONT_STACK },
        },
      },
      radii: {
        small: { value: '0.375rem' },
        medium: { value: '0.75rem' },
        large: { value: '1rem' },
      },
      shadows: {
        small: { value: '0 1px 2px rgba(16, 24, 40, 0.06)' },
        medium: { value: '0 1px 2px rgba(16, 24, 40, 0.04), 0 2px 10px rgba(16, 24, 40, 0.06)' },
        large: { value: '0 12px 28px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06)' },
      },
      components: {

        card: {
          backgroundColor: { value: '{colors.brand.card_background}' },
          borderRadius: { value: '{radii.large}' },
          elevated: {
            backgroundColor: { value: '{colors.brand.card_background}' },
            borderRadius: { value: '{radii.large}' },
            borderWidth: { value: '1px' },
            borderStyle: { value: 'solid' },
            borderColor: { value: '{colors.brand.border_subtle}' },
            boxShadow: { value: '{shadows.medium}' },
          },
          outlined: {
            backgroundColor: { value: '{colors.brand.card_background}' },
            borderRadius: { value: '{radii.large}' },
            borderColor: { value: '{colors.brand.border_strong}' },
          },
        },

        divider: {
          borderColor: { value: '{colors.brand.border_strong}' },
          opacity: { value: '1' },
        },

        heading: {
          color: { value: '{colors.font.primary}' }, // default heading color
          1: { fontWeight: { value: '{fontWeights.extrabold}' } },
          2: { fontWeight: { value: '{fontWeights.extrabold}' } },
          3: { fontWeight: { value: '{fontWeights.bold}' } },
          4: { fontWeight: { value: '{fontWeights.bold}' } },
          5: { fontWeight: { value: '{fontWeights.bold}' } },
          6: { fontWeight: { value: '{fontWeights.bold}' } },
        },

        link: {
          color: { value: '{colors.brand.primary}' },
          visited: { color: { value: '{colors.brand.primary}' } },
          hover: { color: { value: '{colors.brand.primary_hover}' } },
          focus: { color: { value: '{colors.brand.primary_hover}' } },
          active: { color: { value: '{colors.brand.primary_hover}' } },
        },

        selectfield: {
          color: { value: '{colors.brand.primary}' },
          _focus: {
            borderColor: { value: '{colors.brand.primary}' },
          },
          label: {
            color: { value: '{colors.brand.primary}' },
          },
        },

        autocomplete: {
          menu: {
            option: {
              _active: {
                backgroundColor: {
                  value: '{colors.brand.primary}',
                },
              },
            },
          },
        },

        fieldcontrol: {
          color: { value: '{colors.font.primary}' },   // input text color, flips with color mode
          borderColor: { value: '{colors.brand.lighter_gray}' },
          borderRadius: { value: '0.75rem' },
          _focus: {
            borderColor: { value: '{colors.brand.primary}' },
            boxShadow: { value: '0 0 0 3px rgba(58, 186, 96, 0.18)' },
          },
        },
        field: {
          label: {
            color: { value: '{colors.font.primary}' },
          },
        },
        passwordfield: {
          color: { value: '{colors.font.primary}' },
          button: {
            color: { value: '{colors.font.primary}' },
            _hover: {
              backgroundColor: { value: '{colors.brand.primary}' },
              borderColor: { value: '{colors.brand.primary}' },
              color: { value: '{colors.brand.white}' },
            },
            _focus: {
              backgroundColor: { value: '{colors.brand.primary}' },
              borderColor: { value: '{colors.brand.primary}' },
              color: { value: '{colors.brand.white}' },
            },
            _active: {
              backgroundColor: { value: '{colors.brand.primary}' },
              borderColor: { value: '{colors.brand.primary}' },
              color: { value: '{colors.brand.white}' },
            },
          },
        },
        input: {
          _focus: {
            borderColor: { value: '{colors.brand.primary}' },
          },
        },
        authenticator: {
          maxWidth: { value: '32rem' },
          router: {
            borderWidth: { value: '1px' },
            borderStyle: { value: 'solid' },
            borderColor: { value: '{colors.brand.border_subtle}' },
            backgroundColor: { value: '{colors.brand.card_background}' },
            boxShadow: { value: '{shadows.large}' },
          },
          form: {
            padding: { value: '{space.xl.value}' },
          },
          state: {
            inactive: {
              backgroundColor: { value: '{colors.brand.background}' },
            },
          },
          orContainer: {
            color: { value: '{colors.font.primary}' },
            orLine: {
              backgroundColor: { value: '{colors.brand.card_background}' },
            },
          },
          modal: {
            backgroundColor: { value: '{colors.brand.background}' },
          },
        },
        switchfield: {
          thumb: {
            backgroundColor: { value: '{colors.background.primary}' },
            borderColor: { value: '{colors.border.primary}' },
            transition: {
              duration: { value: '{time.long}' },
            },
          },

          track: {
            backgroundColor: { value: '{colors.background.tertiary}' },
            checked: {
              backgroundColor: { value: '{colors.brand.primary}' },
            },
            transition: {
              duration: { value: '{time.long}' },
            },
          },
        },
        // Tabs render as a segmented control; the pill container itself is styled in modern.css.
        tabs: {
          borderWidth: { value: '0' },
          gap: { value: '0.25rem' },
          item: {
            color: { value: '{colors.brand.special_gray}' },
            fontSize: { value: '1rem' },
            fontWeight: { value: '{fontWeights.semibold}' },
            textAlign: { value: 'center' },
            borderWidth: { value: '0' },
            paddingVertical: { value: '0.5rem' },
            paddingHorizontal: { value: '1.125rem' },
            _hover: {
              color: { value: '{colors.brand.primary}'},
              backgroundColor: { value: '{colors.brand.primary_soft}'},
            },
            _focus: {
              color: { value: '{colors.brand.primary}'},
              backgroundColor: { value: '{colors.brand.primary_soft}'},
              borderColor: { value: 'transparent' },
              boxShadow: { value: '0 0 0 2px rgba(58, 186, 96, 0.6)' },
            },
            _active: {
              color: { value: '{colors.brand.white}'},
              borderColor: { value: '{colors.brand.primary}'},
              backgroundColor: { value: '{colors.brand.primary}' },
            },
          },
          panel: {
            paddingBlock: { value: '1rem' },
          },
        },
        accordion: {
          backgroundColor: { value: '{colors.brand.card_background}' },
          item: {
            borderColor: { value: '{colors.brand.lighter_gray}' },
            trigger: {
              backgroundColor: { value: '{colors.brand.card_background}' },
              color: { value: '{colors.font.primary}' },
              _hover: {
                backgroundColor: { value: '{colors.brand.hover_color}' },
                color: { value: '{colors.font.primary}' },
              },
              _focus: {
                borderColor: { value: '{colors.brand.primary}' },
                boxShadow: { value: 'none' },
              },
            },
            content: {
              color: { value: '{colors.font.primary}' },
            },
            icon: {
              color: { value: '{colors.font.primary}' },
            },
          },
        },
        checkbox: {
          button: {
            color: { value: '{colors.brand.white}' },
          },
          icon: {
            backgroundColor: { value: '{colors.brand.primary}' },
          },
          label: {
            color: { value: '{colors.font.primary}'},
            _disabled: {
              color: { value: '{colors.font.primary}' },
            },
          },
        },

        radiogroup: {
          radio: {
            borderWidth: { value: '{borderWidths.small}' },
            borderColor: { value: '{colors.brand.lighter_gray}' },
            _checked: {
              color: { value: '{colors.brand.primary}' },
            },

          },
          legend: {
            color: { value: '{colors.brand.primary}' },
            fontWeight: { value: '{fontWeights.bold}' },
          },
        },

        badge:{

          // Default styles
          color: { value: '{colors.brand.white}'  }, 
          lineHeight: { value: 1 },
          fontWeight: { value: '{fontWeights.semibold.value}' },
          fontSize: { value: '{fontSizes.small.value}' },
          textAlign: { value: 'center' },
          paddingVertical: { value: '{space.xs.value}' },
          paddingHorizontal: { value: '{space.small.value}' },
          backgroundColor: { value: "#770000"},// The default is Far-Right
          // An arbitrarily large value to ensure that the left and right sides of the badge are perfectly rounded for any size variation
          borderRadius: { value: '{radii.xl.value}' },

          // Variations
          info: { // This is Far Left
            color: { value: '{colors.brand.white}' },
            backgroundColor: { value: '#375291' },
          },

          warning: { // This is Liberal
            color: { value: '{colors.brand.white}' },
            backgroundColor: { value: '#427ff7' },
          },

          success: { // This is Moderate
            color: { value: '{colors.brand.white}' },
            backgroundColor: { value: '#6D66FF' },
          },

          error: { // This is Conservative
            color: { value: '{colors.brand.white}' },
            backgroundColor: { value: '#FF4A3B' },
          },

        },

        menu: {
          backgroundColor: { value: '{colors.brand.card_background}' },
          borderColor: { value: '{colors.brand.border_subtle}' },
          borderRadius: { value: '1rem' },
          boxShadow: { value: '{shadows.large}' },

          item: {
            minHeight: { value: '2.75rem' },

          },
        },

        button: {
          // This is the default button color
          fontWeight: { value: '{fontWeights.semibold}' },
          transitionDuration: { value: '{time.short}' },
          backgroundColor: { value: '{colors.brand.full_transparent_black}' },
          color: { value: '{colors.brand.primary}' },
          borderColor: {value: '{colors.brand.primary}'},
          borderRadius: { value: '{radii.xl.value}' },
          _hover: {
            backgroundColor: { value: '{colors.brand.primary}'},
            color: { value: '{colors.brand.white}' },
            borderColor: {value: '{colors.brand.full_transparent_black}'},
          },
          _active:{
            backgroundColor: { value: '{colors.brand.primary}'},
            color: { value: '{colors.brand.white}' },
            borderColor: {value: '{colors.brand.full_transparent_black}'},
          },
          _focus: {                                  // ← add this
            backgroundColor: { value: '{colors.brand.primary}' },
            color:           { value: '{colors.brand.white}' },
            borderColor:     { value: '{colors.brand.full_transparent_black}' },
            boxShadow:       { value: 'none' },    // ← kill the default focus ring :contentReference[oaicite:0]{index=0}
          },
          menu: {
            backgroundColor: { value: '{colors.brand.custom_purple}' },
            _hover: {
              backgroundColor: { value: '{colors.brand.custom_purple}'},
              color: { value: '{colors.brand.white}' },
            },
            _active:{
              backgroundColor: {value: '{colors.brand.custom_purple}'},
              color: {value: '{colors.brand.white}'}
            },
            _focus: {                                  // ← add this
              backgroundColor: { value: '{colors.brand.custom_purple}' },
              color:           { value: '{colors.brand.white}' },
            },
          },

          primary: {
            backgroundColor: { value: '{colors.brand.special_black}' },
            color: { value: '{colors.brand.white}' },
            borderColor: {value: '{colors.brand.special_black}'},
            _hover: {
              backgroundColor: { value: '{colors.brand.primary}'},
              color: { value: '{colors.brand.white}' },
              borderColor: {value: '{colors.brand.full_transparent_black}'},
            },
            _active:{
              borderColor: {value: '{colors.brand.full_transparent_black}'},
              backgroundColor: {value: '{colors.brand.primary}'},
              color: {value: '{colors.brand.white}'}
            },
            _focus: {                                  // ← add this
              backgroundColor: { value: '{colors.brand.primary}' },
              color:           { value: '{colors.brand.white}' },
              borderColor:     { value: '{colors.brand.full_transparent_black}' },
              boxShadow:       { value: 'none' },
            },
          },
          link: {
            backgroundColor: { value: '{colors.brand.primary}'},
            color: { value: '{colors.brand.white}' },
            borderColor: {value: '{colors.brand.primary}'},

            _hover: {
                backgroundColor: { value: '{colors.brand.primary_hover}' },
                color: { value:'{colors.brand.white}'},
                borderColor: {value: '{colors.brand.primary_hover}'},
              },
            _active:{
              borderColor: {value: '{colors.brand.primary_hover}'},
              backgroundColor: {value: '{colors.brand.primary_hover}'},
              color: {value: '{colors.brand.white}'}
            },
            _focus: {                                  // ← add this
              borderColor: {value: '{colors.brand.primary}'},
              backgroundColor: {value: '{colors.brand.primary}'},
              color: {value: '{colors.brand.white}'},
              boxShadow:       { value: 'none' },
            },
            _disabled: {
              borderColor: {value: '{colors.brand.lighter_gray}'},
              backgroundColor: {value: '{colors.brand.lighter_gray}'},
              color: {value: '{colors.brand.white}'},
            },
          },
          // Neutral "surface" button. Several list items (politicians, votes) render as this variation.
          destructive: {
            backgroundColor: { value: '{colors.brand.card_background}' },
            color: { value: '{colors.brand.primary}' },
            borderColor: {value: '{colors.brand.border_strong}'},

            _hover: {
                backgroundColor: { value: '{colors.brand.hover_color}' },
                color: { value:'{colors.brand.primary}' },
                borderColor: {value: '{colors.brand.primary}'},
              },
            _active:{
              backgroundColor: { value: '{colors.brand.hover_color}' },
              color: { value:'{colors.brand.primary}' },
              borderColor: {value: '{colors.brand.primary}'},
            },
            _focus:{
              backgroundColor: { value: '{colors.brand.hover_color}' },
              color: { value:'{colors.brand.primary}' },
              borderColor: {value: '{colors.brand.primary}'},
              boxShadow:       { value: 'none' },
            },
            _loading:{
              backgroundColor: { value: '{colors.brand.background}' },
              color: { value:'{colors.brand.primary}' },
              borderColor: {value: '{colors.brand.primary}'},
            },
          },
        },
      },
    },
    overrides: [
      {
        colorMode: 'dark',
        tokens: {
          colors: {
            brand: {
              background: { value: '#18191C' }, // dark override
              card_background: {value: '#232428'},
              explore_issues_background: {value: "#3ABA60"},
              explore_issues_text: {value: '#FFFFFF'},
              explore_issues_button_background: {value: '#353535'},
              light_primary_green: {value: '#1E3627'},
              special_gray: {value: '#B5B7BE'},
              nav_header: {value: '#111214'},
              custom_purple : {value: '#7166EE'},
              custom_purple_shade : {value: '#7166EE20'},
              chart_color: {value: '#FFFFFF'},
              hover_color: { value: '#2C2D32' },
              primary_hover: { value: '#46C96D' },
              primary_soft: { value: 'rgba(58, 186, 96, 0.16)' },
              purple_soft: { value: 'rgba(139, 130, 241, 0.18)' },
              surface_muted: { value: '#2A2B30' },
              border_subtle: { value: 'rgba(255, 255, 255, 0.08)' },
              border_strong: { value: 'rgba(255, 255, 255, 0.18)' },
            },
            font: {
              primary: { value: '#FFFFFF'},
              secondary: {value: '#FFFFFF'},
            },
          },
          shadows: {
            small: { value: '0 1px 2px rgba(0, 0, 0, 0.4)' },
            medium: { value: '0 1px 2px rgba(0, 0, 0, 0.35), 0 4px 14px rgba(0, 0, 0, 0.25)' },
            large: { value: '0 16px 36px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)' },
          },
          components: {
            heading: { color: { value: '#FFFFFF'} },
            text:    { color: { value: '#FFFFFF' } },

            accordion: {
              backgroundColor: { value: '{colors.brand.card_background}' },
              item: {
                borderColor: { value: '{colors.brand.border_strong}' },
                trigger: {
                  backgroundColor: { value: '{colors.brand.card_background}' },
                  color: { value: '{colors.font.primary}' },
                  _hover: {
                    backgroundColor: { value: '{colors.brand.hover_color}' },
                    color: { value: '{colors.font.primary}' },
                  },
                  _focus: {
                    borderColor: { value: '{colors.brand.primary}' },
                    boxShadow: { value: 'none' },
                  },
                },
                content: {
                  color: { value: '{colors.font.primary}' },
                },
                icon: {
                  color: { value: '{colors.font.primary}' },
                },
              },
            },
            checkbox: {
              label: {
                color: { value: '{colors.font.primary}'},
                _disabled: {
                  color: { value: '{colors.font.primary}' },
                },
              },
            },
            autocomplete: {
              menu: {

                option: {
                  backgroundColor: { value: '{colors.brand.card_background}' },
                  _active: {
                    backgroundColor: {
                      value: '{colors.brand.primary}',
                    },
                  },
                },
              },
            },
            fieldcontrol: {
              color: { value: '{colors.font.primary}' },   // input text color
              borderColor: { value: '{colors.brand.border_strong}' },
            },
            selectfield: {
              color: { value: '{colors.font.primary}' },
            },
            select: {
              backgroundColor: { value: '{colors.brand.card_background}' },
              color: { value: '{colors.font.primary}' },
            },
            authenticator: {
              router: {
                borderColor: { value: '{colors.brand.border_subtle}' },
                backgroundColor: { value: '{colors.brand.card_background}' },
              },
              state: {
                inactive: {
                  backgroundColor: { value: '{colors.brand.nav_header}' },
                },
              },
              orContainer: {
                color: { value: '{colors.font.primary}' },
                orLine: {
                  backgroundColor: { value: '{colors.brand.border_strong}' },
                },
              },
              modal: {
                backgroundColor: { value: '{colors.brand.background}' },
              },
            },
            button: {
              // Light "inverted" primary reads clearly on dark surfaces.
              primary: {
                backgroundColor: { value: '#ECECEE' },
                color: { value: '#18191C' },
                borderColor: {value: '#ECECEE'}, _hover: { backgroundColor: { value: '{colors.brand.primary}'},
                  color: { value: '{colors.brand.white}' },
                  borderColor: {value: '{colors.brand.full_transparent_black}'},
                },
                _active:{
                  borderColor: {value: '{colors.brand.full_transparent_black}'},
                  backgroundColor: {value: '{colors.brand.primary}'},
                  color: {value: '{colors.brand.white}'}
                },
                _focus: {                                  // ← add this
                  backgroundColor: { value: '{colors.brand.primary}' },
                  color:           { value: '{colors.brand.white}' },
                  borderColor:     { value: '{colors.brand.full_transparent_black}' },
                  boxShadow:       { value: 'none' },
                },
              },
              destructive: {
                backgroundColor: { value: '{colors.brand.card_background}' },
                color: { value: '{colors.brand.primary}' },
                borderColor: {value: '{colors.brand.border_strong}'},

                _hover: {
                    backgroundColor: { value: '{colors.brand.hover_color}' },
                    color: { value:'{colors.brand.primary}' },
                    borderColor: {value: '{colors.brand.primary}'},
                  },
                _active:{
                  backgroundColor: { value: '{colors.brand.hover_color}' },
                  color: { value:'{colors.brand.primary}' },
                  borderColor: {value: '{colors.brand.primary}'},
                },
                _focus:{
                  backgroundColor: { value: '{colors.brand.hover_color}' },
                  color: { value:'{colors.brand.primary}' },
                  borderColor: {value: '{colors.brand.primary}'},
                  boxShadow:       { value: 'none' },
                },
                _loading:{
                  backgroundColor: { value: '{colors.brand.background}' },
                  color: { value:'{colors.brand.primary}' },
                  borderColor: {value: '{colors.brand.primary}'},
                },
              },
            }
          },
        },
      },
    ],

  });
